import React, { useEffect, useRef } from 'react';
import { useNavigate } from 'react-router-dom';
import { toast } from 'sonner';
import axios from 'axios';
import { Loader2 } from 'lucide-react';

/**
 * Career portal Google-Auth landing page.
 *
 * Reached whenever the URL contains `#session_id=<sid>` (set by Emergent
 * after the user completes Google consent). Exchanges the sid for our
 * career JWT via POST /api/career/auth/google/session, stores the token
 * the same way the password login does, then navigates to the dashboard
 * preserving any `?role=` or `?app=` deep-link params.
 *
 * REMINDER: DO NOT HARDCODE THE URL, OR ADD ANY FALLBACKS OR REDIRECT URLS,
 * THIS BREAKS THE AUTH.
 */
export default function CareerAuthCallback() {
  const navigate = useNavigate();
  const hasProcessed = useRef(false);

  useEffect(() => {
    if (hasProcessed.current) return;
    hasProcessed.current = true;

    const hash = window.location.hash || '';
    const match = hash.match(/session_id=([^&]+)/);
    const sessionId = match ? decodeURIComponent(match[1]) : null;

    // Also carry forward any query params the user had at click time
    // (e.g. ?role=<id> or ?app=<id>).
    const carryQuery = window.location.search || '';

    if (!sessionId) {
      toast.error('Google sign-in failed — no session id returned');
      navigate('/career/login');
      return;
    }

    (async () => {
      try {
        const res = await axios.post('/api/career/auth/google/session', {
          session_id: sessionId,
        });
        localStorage.setItem('career_token', res.data.token);
        localStorage.setItem('career_user', JSON.stringify(res.data.user));
        // Strip the fragment from the URL so a refresh doesn't retry.
        window.history.replaceState(
          null,
          '',
          window.location.pathname + carryQuery,
        );
        toast.success(res.data.is_new ? 'Welcome to Girgit!' : 'Signed in with Google');
        navigate('/career/dashboard' + carryQuery);
      } catch (err) {
        toast.error(err?.response?.data?.detail || 'Google sign-in failed');
        navigate('/career/login');
      }
    })();
  }, [navigate]);

  return (
    <div
      className="min-h-screen flex items-center justify-center bg-neutral-50"
      data-testid="career-auth-callback"
    >
      <div className="text-center">
        <Loader2 className="w-10 h-10 animate-spin text-indigo-600 mx-auto" />
        <p className="mt-4 text-neutral-600 text-sm">Finishing sign-in…</p>
      </div>
    </div>
  );
}
